/**
 * Landing page after the API has verified the email and redirected the
 * browser here. By the time we render, the BetterAuth session cookie
 * is already set (autoSignInAfterVerification: true on the server).
 * useSession() picks it up; we just need to wait a beat and navigate.
 *
 * If something went wrong (token expired, already used, etc.) the API
 * redirects here with `?error=<code>` in the query string — surface that.
 *
 * IMPORTANT: this page does NOT call any verify-email endpoint. The
 * verify-email route in BetterAuth is a GET on the API host that
 * verifies + sets cookie + redirects (see
 * node_modules/better-auth/dist/api/routes/email-verification.mjs).
 * The SPA never sees the token.
 */
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/shared/auth/AuthProvider';
import { Button } from '@/interfaces/learner/ui';
import AuthLayout from '@/shared/auth/AuthLayout';
import Logomark from '@/shared/components/Logomark';

export default function EmailVerifiedPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');
  const { isAuthed, loading } = useAuth();

  useEffect(() => {
    if (error || loading) return;
    if (isAuthed) {
      // Verified + cookie set + session loaded → off to the dashboard.
      const t = setTimeout(() => navigate('/', { replace: true }), 500);
      return () => clearTimeout(t);
    }
    // Edge case: page loaded without `?error=` AND without a session.
    // Possible causes: user bookmarked /email-verified, the verify cookie
    // didn't propagate cross-domain, the user opened the link in a
    // different browser, etc. Send them to /login after a short hold so
    // the "Email verified" message is visible briefly before the bounce.
    const t = setTimeout(() => navigate('/login', { replace: true }), 1500);
    return () => clearTimeout(t);
  }, [error, isAuthed, loading, navigate]);

  if (error) {
    const message =
      error === 'token_expired' || error === 'TOKEN_EXPIRED'
        ? 'This verification link has expired. Sign up again or request a new email.'
        : error === 'invalid_token' || error === 'INVALID_TOKEN'
          ? 'This verification link is invalid or has already been used.'
          : 'Verification failed. Please try again or request a new email.';

    return (
      <AuthLayout>
        <div className="text-center mb-8">
          <div className="mb-4 flex justify-center">
            <Logomark size={48} />
          </div>
          <h1 className="text-2xl font-bold">Verification failed</h1>
        </div>
        <div className="space-y-4 text-center">
          <p className="text-sm">{message}</p>
          <Button variant="primary" size="md" onPress={() => navigate('/login')}>
            Back to sign in
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="text-center mb-8">
        <div className="mb-4 flex justify-center">
          <Logomark size={48} />
        </div>
        <h1 className="text-2xl font-bold">Email verified</h1>
      </div>
      <p className="text-sm text-center">Taking you to your dashboard…</p>
    </AuthLayout>
  );
}
